import { useEffect, useRef, useState } from 'react';
import { NUKE_ALARM_MS, NUKE_FLIGHT_MS, type AdminAction, type Color, type GameState, type IconId } from '@ic/engine';
import { sfx } from '../lib/sound';
import { settings, useStore } from '../lib/store';
import { Icon } from './Icons';
import { PieceIcon, type PieceColor } from './Pieces';

/** Side panel for admins in a running game: Nuke, Spawn a piece, Win. */
export function AdminPanel({ state, color, picking, onPickType, onNuke, onWin, canAdvise, hintBusy, onBest, auto, onAuto }: {
  state: GameState;
  color: Color;
  /** Piece type waiting for its square, if any. */
  picking: string | null;
  onPickType: (type: string | null) => void;
  onNuke: () => void;
  onWin: () => void;
  canAdvise: boolean;
  hintBusy: boolean;
  onBest: () => void;
  auto: boolean;
  onAuto: (on: boolean) => void;
}) {
  const { pieceSet } = useStore(settings);
  const [spawnOpen, setSpawnOpen] = useState(false);
  const types = Object.values(state.rs.pieces).filter((d) => !d.royal);
  const over = !!state.result;
  return (
    <div className="admin-panel" aria-label="Admin panel">
      <div className="admin-head"><Icon name="gear" size={14} /> Admin</div>
      <div className="admin-buttons">
        <button className="btn danger sm" disabled={over} onClick={() => { if (confirm('Drop a nuke? Every piece except your king is destroyed and you win.')) onNuke(); }}>
          ☢ Nuke
        </button>
        <button className={`btn secondary sm ${spawnOpen ? 'on' : ''}`} disabled={over} onClick={() => { setSpawnOpen(!spawnOpen); onPickType(null); }}>
          <Icon name="plus" size={14} /> Spawn
        </button>
        <button className="btn secondary sm" disabled={over} onClick={() => { if (confirm('Win this game now?')) onWin(); }}>
          <Icon name="check" size={14} /> Win
        </button>
        <button className="btn secondary sm" disabled={over || !canAdvise || hintBusy} onClick={onBest} title="Show the strongest move for the side to move">
          <Icon name="check" size={14} /> {hintBusy ? 'Thinking…' : 'Best move'}
        </button>
        <button className={`btn secondary sm ${auto ? 'on' : ''}`} disabled={over} onClick={() => onAuto(!auto)} title="Play the best move automatically on every turn">
          <Icon name="next" size={14} /> Autoplay {auto ? 'on' : 'off'}
        </button>
      </div>
      {spawnOpen && !over && (
        <div className="admin-spawn">
          <div className="spawn-types">
            {types.map((d) => (
              <button key={d.id} type="button" className={picking === d.id ? 'on' : ''} title={d.name} onClick={() => onPickType(picking === d.id ? null : d.id)}>
                <PieceIcon icon={d.icon} color={color} set={pieceSet} />
              </button>
            ))}
          </div>
          <span className="muted small">{picking ? `Click an empty square for the ${state.rs.pieces[picking]?.name.toLowerCase()}.` : 'Pick a piece, then a square.'}</span>
        </div>
      )}
    </div>
  );
}

/** A piece caught in the blast, positioned in percent of the board. */
export interface NukePiece { sq: number; x: number; y: number; size: number; icon: IconId; color: PieceColor }


/** A B-52 style bomber seen from above, nose pointing right. */
function Bomber() {
  const hull = '#4d565f';
  const hi = '#6c7882';
  const dark = '#343b42';
  // One wing and its two engine pods; the other side is mirrored around y = 70.
  const side = (
    <g>
      <path d="M118 63 L72 2 L56 2 L88 63 Z" fill={hull} stroke={dark} strokeWidth="0.8" />
      <path d="M112 62 L74 8 L68 8 L96 62 Z" fill={hi} opacity="0.35" />
      <path d="M31 66 L11 41 L3 41 L15 66 Z" fill={hull} stroke={dark} strokeWidth="0.8" />
      {[[100, 40], [86, 21]].map(([x, y], i) => (
        <g key={i}>
          <rect x={x - 2} y={y - 1} width="22" height="7.5" rx="3.5" fill={dark} />
          <rect x={x + 14} y={y} width="6" height="5.5" rx="2.7" fill="#0d1013" />
          <rect x={x + 3} y={y + 0.5} width="2.4" height="4.5" fill="#9aa5ae" opacity="0.7" />
          <rect x={x + 8} y={y + 0.5} width="2.4" height="4.5" fill="#9aa5ae" opacity="0.7" />
        </g>
      ))}
      <path d="M118 63 L92 28" stroke={dark} strokeWidth="0.6" fill="none" />
    </g>
  );
  return (
    <svg className="bomber" viewBox="0 0 210 140" aria-hidden="true">
      {side}
      <g transform="translate(0 140) scale(1 -1)">{side}</g>
      <path d="M206 70 Q202 64 182 63 L30 64 L6 67 L6 73 L30 76 L182 77 Q202 76 206 70 Z" fill={hull} stroke={dark} strokeWidth="1" />
      <path d="M196 70 Q192 66 176 65.5 L40 66 L40 70 Z" fill={hi} opacity="0.45" />
      <path d="M6 67 L-2 70 L6 73 Z" fill={dark} />
      <ellipse cx="184" cy="70" rx="9" ry="3.4" fill="#10161b" />
      <ellipse cx="186" cy="69" rx="4.5" ry="1.5" fill="#6fb1d6" opacity="0.8" />
      <path d="M150 64 L150 76 M110 64.5 L110 75.5 M70 65 L70 75" stroke={dark} strokeWidth="0.6" />
      <circle cx="132" cy="70" r="1.6" fill="#c9d2d8" opacity="0.6" />
    </svg>
  );
}

/** The bomb: fat body, fins, a red band. */
function Bomb() {
  return (
    <svg className="bomb-drop" viewBox="0 0 40 100" aria-hidden="true">
      <path d="M20 2 Q34 12 34 40 L34 66 Q34 78 20 82 Q6 78 6 66 L6 40 Q6 12 20 2 Z" fill="#3a4148" stroke="#1b1f23" strokeWidth="1.5" />
      <path d="M12 10 Q9 30 11 64" stroke="#8a96a0" strokeWidth="2" fill="none" opacity="0.6" />
      <rect x="6" y="46" width="28" height="6" fill="#d33a2c" />
      <path d="M6 70 L-2 96 L8 88 Z M34 70 L42 96 L32 88 Z M17 80 L17 100 L23 100 L23 80 Z" fill="#2a3036" stroke="#1b1f23" strokeWidth="1" />
    </svg>
  );
}

/**
 * A bomber crosses the board, drops a nuke, and the whole board goes up: flash, shock waves, fireball,
 * mushroom cloud and a shaking board. `full` plays the whole flight and calls `onImpact` when the bomb
 * hits; `impact` (seen by the other players) starts at the explosion.
 */
export function NukeOverlay({ run, phase, pieces = [], onImpact, onDone }: { run: number; phase: 'full' | 'impact'; pieces?: NukePiece[]; onImpact?: () => void; onDone: () => void }) {
  const { animations, pieceSet } = useStore(settings);
  const ref = useRef<HTMLDivElement>(null);
  const lead = phase === 'full' && animations ? NUKE_ALARM_MS + NUKE_FLIGHT_MS : 0;
  const [stage, setStage] = useState<'alarm' | 'fly' | 'boom'>(lead === 0 ? 'boom' : 'alarm');
  const boom = stage === 'boom';
  useEffect(() => {
    const host = ref.current?.parentElement ?? null;
    const timers = [
      setTimeout(() => { setStage('fly'); sfx.plane(); }, NUKE_ALARM_MS),
      setTimeout(() => {
        setStage('boom');
        sfx.nuke();
        onImpact?.();
        if (animations) host?.classList.add('nuke-shake');
      }, lead),
      setTimeout(onDone, lead + (animations ? 5600 : 600)),
    ];
    if (phase === 'full' && animations) { sfx.alarm(); host?.classList.add('nuke-dim'); }
    return () => { timers.forEach(clearTimeout); host?.classList.remove('nuke-shake', 'nuke-dim'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);
  return (<>
    <div ref={ref} className={`nuke-overlay ${boom ? 'boom' : ''} ${animations ? '' : 'still'}`} aria-hidden="true">
      {stage === 'alarm' && (
        <div className="nuke-alarm">
          <i className="siren a" /><i className="siren b" />
          <div className="alarm-banner"><b>Nuclear strike incoming</b><span>Brace for impact</span></div>
        </div>
      )}
      {stage === 'fly' && (
        <>
          <div className="contrail" />
          <Bomber />
          <Bomb />
        </>
      )}
      {boom && (
        <>
          <div className="nuke-halo" />
          <div className="nuke-wave one" />
          <div className="nuke-wave two" />
          <svg className="nuke-defs" width="0" height="0" aria-hidden="true">
            <filter id="nuke-turb" x="-25%" y="-25%" width="150%" height="150%">
              <feTurbulence type="fractalNoise" baseFrequency="0.02 0.03" numOctaves="3" seed="7" result="n" />
              <feDisplacementMap in="SourceGraphic" in2="n" scale="26" xChannelSelector="R" yChannelSelector="G" />
            </filter>
            <filter id="nuke-lumps" x="-30%" y="-10%" width="160%" height="120%">
              <feTurbulence type="fractalNoise" baseFrequency="0.045 0.022" numOctaves="4" seed="3" result="n" />
              <feDisplacementMap in="SourceGraphic" in2="n" scale="34" xChannelSelector="R" yChannelSelector="G" />
            </filter>
          </svg>
          <div className="nuke-cloud">
            <i className="stem" />
            <i className="cap" />
          </div>
          <div className="nuke-base"><i /><i /><i /><i /></div>
          <div className="nuke-flash" />
          <div className="nuke-dust" />
        </>
      )}
    </div>
    {boom && animations && (
      <div className="nuke-pieces" aria-hidden="true">
        {pieces.map((p, i) => {
          // Seen from the middle of the blast: near pieces go first, half burst, half are thrown off the board.
          const dx = p.x - 50, dy = p.y - 78;
          const dist = Math.hypot(dx, dy) || 1;
          const seed = ((p.sq + 1) * 2654435761) >>> 0;
          const fly = seed % 2 === 0;
          const power = 5 + ((seed >>> 4) % 50) / 10;
          const ang = Math.atan2(dy, dx) + (((seed >>> 9) % 100) / 100 - 0.5) * 0.7;
          const style = {
            left: `${p.x}%`, top: `${p.y}%`, width: `${p.size}%`, height: `${p.size}%`,
            animationDelay: `${Math.round(dist * 2)}ms`,
            '--dx': `${Math.round(Math.cos(ang) * power * 100)}%`,
            '--dy': `${Math.round((Math.sin(ang) * power - 2.2) * 100)}%`,
            '--rot': `${(((seed >>> 13) % 1000) / 1000 - 0.5) * 1500}deg`,
          } as React.CSSProperties;
          return (
            <div key={i} className={`nuke-piece ${fly ? 'fly' : 'boom'}`} style={style}>
              <PieceIcon icon={p.icon} color={p.color} set={pieceSet} />
            </div>
          );
        })}
      </div>
    )}
  </>);
}
